import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '../../../../app.actions';
import * as fromAppRoot from '../../../../app.reducers';
import * as fromReporting from '../../reporting.reducers';

import { INVESTMENT_LIMITS, INVESTMENT_LIMITS_3A } from '@app/app.constants';
import { PromoCampaignItem } from '@shared/models/DigitalPromo';
import { Notification, NotificationAttributes } from '@shared/models/Reporting';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { TrackingService } from 'src/app/modules/shared/services/tracking.service';

@Component({
  selector: 'app-reporting-dashboard',
  templateUrl: './reporting-dashboard.component.html',
  styleUrls: ['./reporting-dashboard.component.scss'],
  standalone: false
})
export class ReportingDashboardComponent implements OnDestroy, OnInit {
  protected readonly onDestroy$ = new Subject<void>();
  customer: Customer;
  currentAccount: CustomerAccount;
  isInitialised: boolean;
  daysDifference: number;
  endValuesIssues = false;
  technicalIssuesRecognized = false;
  showSuperhuman = false;
  superhuman: NotificationAttributes;
  showRecheck = false;
  riskRecheck: Notification;
  notificationId: number;
  minFundingAmount = 0;
  stillNeededFundingAmount = 0;
  is3A: boolean;
  promoCampaigns$: Observable<PromoCampaignItem[]>;

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private router: Router,
    private amplitudeService: AmplitudeService
  ) {
    this.store
      .pipe(select(fromAppRoot.selectCustomerAndCurrentInvestAccount), takeUntil(this.onDestroy$))
      .subscribe(({ customer, currentAccountDetail }) => {
        if (!customer || !customer.email || currentAccountDetail?.id === '') {
          return;
        }
        this.customer = customer;
        this.currentAccount = currentAccountDetail;
        this.is3A = currentAccountDetail?.kind === '3a';
      });
    this.store.pipe(select(fromReporting.selectEndValues), takeUntil(this.onDestroy$)).subscribe(({ deposits_value, withdrawal_all }) => {
      const minFundingAmount = this.is3A ? INVESTMENT_LIMITS_3A : INVESTMENT_LIMITS;
      this.minFundingAmount = minFundingAmount.initial[this.customer.app_location];
      this.stillNeededFundingAmount = this.minFundingAmount - (deposits_value - withdrawal_all);
    });
    this.store.pipe(select(fromReporting.selectEndValuesStatus), takeUntil(this.onDestroy$)).subscribe((status) => {
      this.endValuesIssues = status;
    });

    this.store
      .pipe(
        select(fromAppRoot.selectThirdPartyServices(`disable_reporting_${this.customer.app_location}_${this.currentAccount.kind}`)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((service: ThirdPartyServiceItem) => {
        if (!service) {
          return;
        }
        this.technicalIssuesRecognized = service.attributes.value;
      });
    this.store.pipe(select(fromReporting.selectIsInitialised), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isInitialised = value;
    });
    this.store.pipe(select(fromReporting.selectDaysDifference), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.daysDifference = value;
    });

    this.store.pipe(select(fromReporting.selectSuperhumanNotification), takeUntil(this.onDestroy$)).subscribe((notification) => {
      if (!notification || !notification.length) {
        this.showSuperhuman = false;
        return;
      }
      this.superhuman = notification[0].attributes;
      this.notificationId = notification[0].id;
      this.showSuperhuman = true;
    });

    this.store.pipe(select(fromReporting.selectRiskrecheckNotification), takeUntil(this.onDestroy$)).subscribe((notification) => {
      if (!notification || !notification.length) {
        this.showRecheck = false;
        return;
      }

      this.riskRecheck = notification[0];
      this.showRecheck = true;
    });
    this.promoCampaigns$ = this.store.select(fromAppRoot.selectPromoCampaigns);
  }

  ngOnInit(): void {
    this.store.dispatch(AppRootActions.getPromoCampaigns());
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  track() {
    this.trackingService.trackActivity('Performance => Adjust investment', true);
    this.amplitudeService.trackEvent('Savings Plan Opened', { action_source: 'bottom_navigation', kind: this.currentAccount.kind });
  }

  onShowReasons() {
    this.trackingService.trackActivity('[Button] Sp Banner: Show 3 reasons why');
    void this.router.navigate(['/adjust-investment'], { queryParams: { reasons: true }, skipLocationChange: true });
  }

  onShowAccountDetails() {
    this.trackingService.trackActivity('[Button] Sp Banner: Show account details');
    void this.router.navigate(['/adjust-investment'], { queryParams: { accountDetails: true }, skipLocationChange: true });
  }
}
